import { INavigationLink } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/INavigationLink";
import { Link, Anchor } from "@faire/web/ui/LinkAnchor";
import trackClick from "@faire/web-api--source/endpoints/www/api/click-track/post";
import { trackRetailersTopNavClick } from "@faire/web-api--source/events/retailers/click/topNav";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import React from "react";

interface IProps {
  children?: React.ReactNode | undefined;
  link: Pick<INavigationLink, "name" | "to" | "categoryPathIdentifier">;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onFocus?: () => void;
  className?: string;
  dataTestId?: string;
  style?: React.CSSProperties;
  disableNavigation?: boolean;
}

const COMPONENT_NAME = "desktop-header-category-dropdown";
const MOST_POPULAR_FILTER_KEY = "most_popular";

export const NavigationLink: React.FC<IProps> = ({
  children,
  link,
  onClick,
  onMouseEnter,
  onFocus,
  className,
  dataTestId,
  style,
  disableNavigation,
}) => {
  const onClickWithTracking = React.useCallback(() => {
    if (link.categoryPathIdentifier) {
      let eventToken = link.categoryPathIdentifier;
      if (link.to?.includes(MOST_POPULAR_FILTER_KEY)) {
        eventToken = `${eventToken}|${MOST_POPULAR_FILTER_KEY}`;
      }
      trackRetailersTopNavClick(eventToken);
      trackClick(
        IClickInfo.build({
          container_name: COMPONENT_NAME,
          token: link.categoryPathIdentifier,
          type: IClickInfo.Type.CATEGORY,
        })
      );
    }
    onClick?.();
  }, [link, onClick]);

  if (!link.to || disableNavigation) {
    return (
      <div
        style={style}
        onClick={onClickWithTracking}
        onMouseEnter={onMouseEnter}
        onFocus={onFocus}
        className={className}
        aria-label={link.name}
        data-test-id={dataTestId}
      >
        {children}
      </div>
    );
  }

  return link.to.startsWith("http") ? (
    <Anchor
      href={link.to}
      style={style}
      onClick={onClickWithTracking}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      className={className}
      aria-label={link.name}
      data-test-id={dataTestId}
    >
      {children}
    </Anchor>
  ) : (
    <Link
      to={link.to}
      style={style}
      onClick={onClickWithTracking}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      className={className}
      aria-label={link.name}
      data-test-id={dataTestId}
    >
      {children}
    </Link>
  );
};
